import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { App } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { Fingerprint, LockKeyhole, LogOut } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "../router";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../lib/api";
import { bool, isRecord, unwrapData } from "../lib/data";
import { safeDeepLinkPath } from "./deep-links";
import { isNativeApp, storeNativePushToken } from "./platform";

const biometricPreferenceKey = "f-bucks-biometric-unlock";

export function NativeBridge({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const native = isNativeApp();
  const [locked, setLocked] = useState(native);
  const [biometricError, setBiometricError] = useState("");
  const backgroundAt = useRef<number | null>(null);
  const initialUnlockChecked = useRef(false);

  const unlock = useCallback(async () => {
    if (!native) return;
    setBiometricError("");
    try {
      const available = await BiometricAuth.checkBiometry();
      if (!available.isAvailable) {
        setBiometricError(
          "Biometrie oder Gerätesperre ist nicht mehr verfügbar. Melde dich ab oder richte sie erneut ein.",
        );
        setLocked(true);
        return;
      }
      await BiometricAuth.authenticate({
        reason: "Bestehende F‑Bucks-Sitzung entsperren",
        androidTitle: "F‑Bucks entsperren",
        androidSubtitle: "Bestätige deine Identität auf diesem Gerät.",
        cancelTitle: "Abbrechen",
        allowDeviceCredential: true,
      });
      setLocked(false);
    } catch {
      setBiometricError("Entsperren fehlgeschlagen oder abgebrochen.");
      setLocked(true);
    }
  }, [native]);

  useEffect(() => {
    if (!native || initialUnlockChecked.current) return;
    initialUnlockChecked.current = true;
    void Preferences.get({ key: biometricPreferenceKey })
      .then(({ value }) => {
        if (value === "true") {
          setLocked(true);
          return unlock();
        }
        setLocked(false);
      })
      .catch(() => {
        setBiometricError(
          "Die lokale Entsperreinstellung konnte nicht geprüft werden. Melde dich sicher ab.",
        );
        setLocked(true);
      });
  }, [native, unlock]);

  useEffect(() => {
    if (!native) return;
    void SplashScreen.hide();
    let disposed = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];

    void App.addListener("appUrlOpen", ({ url }) => {
      const path = safeDeepLinkPath(url);
      if (path) void navigate(path);
    }).then((handle) => {
      if (disposed) void handle.remove();
      else handles.push(handle);
    });

    void App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        backgroundAt.current = Date.now();
        return;
      }
      if (backgroundAt.current && Date.now() - backgroundAt.current > 30_000) {
        void Preferences.get({ key: biometricPreferenceKey }).then(({ value }) => {
          if (value === "true" && auth.state.kind === "authenticated") {
            setLocked(true);
            void unlock();
          }
        });
      }
    }).then((handle) => {
      if (disposed) void handle.remove();
      else handles.push(handle);
    });

    return () => {
      disposed = true;
      handles.forEach((handle) => void handle.remove());
    };
  }, [auth.state.kind, native, navigate, unlock]);

  useEffect(() => {
    if (!native || auth.state.kind !== "authenticated" || auth.state.viewer.status !== "ACTIVE")
      return;
    let disposed = false;
    const handles: Array<{ remove: () => Promise<void> }> = [];
    const setup = async () => {
      try {
        const capabilitiesResponse = await api.get<unknown>("/system/capabilities");
        const body = unwrapData(capabilitiesResponse);
        const capabilities = isRecord(body) ? body : {};
        if (!bool(capabilities.firebaseConfigured)) return;
        const registrationHandle = await PushNotifications.addListener("registration", (token) => {
          void storeNativePushToken(token.value);
          void api.post("/account/push-tokens", { token: token.value, platform: "ANDROID" });
        });
        if (disposed) void registrationHandle.remove();
        else handles.push(registrationHandle);
        const actionHandle = await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (event) => {
            const data = event.notification.data as unknown;
            const raw =
              isRecord(data) && typeof data.url === "string" ? data.url : event.notification.link;
            if (!raw) return;
            const path = raw.startsWith("/") ? raw : safeDeepLinkPath(raw);
            if (path) void navigate(path);
          },
        );
        if (disposed) void actionHandle.remove();
        else handles.push(actionHandle);
        const permission = await PushNotifications.requestPermissions();
        if (permission.receive === "granted") await PushNotifications.register();
      } catch {
        // In-app notifications remain the complete fallback when native push is unavailable.
      }
    };
    void setup();
    return () => {
      disposed = true;
      handles.forEach((handle) => void handle.remove());
    };
  }, [auth.state, native, navigate]);

  if (!locked) return <>{children}</>;
  return (
    <div
      className="native-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="native-lock-title"
    >
      <img src="/logo-fbucks.svg" alt="" />
      <span className="icon-disc">
        <LockKeyhole aria-hidden="true" />
      </span>
      <h1 id="native-lock-title">Sitzung gesperrt</h1>
      <p>Deine bestehende Sitzung bleibt erhalten. Entsperre sie sicher auf diesem Gerät.</p>
      {biometricError && (
        <p className="notice notice--error" role="alert">
          {biometricError}
        </p>
      )}
      <button className="button button--primary" type="button" onClick={() => void unlock()}>
        <Fingerprint aria-hidden="true" />
        Biometrisch entsperren
      </button>
      <button className="button button--ghost" type="button" onClick={() => void auth.logout()}>
        <LogOut aria-hidden="true" />
        Stattdessen abmelden
      </button>
    </div>
  );
}

export async function setBiometricUnlockEnabled(enabled: boolean): Promise<void> {
  if (!isNativeApp()) return;
  if (enabled) {
    const available = await BiometricAuth.checkBiometry();
    if (!available.isAvailable)
      throw new Error("Auf diesem Gerät ist keine Biometrie eingerichtet.");
    await BiometricAuth.authenticate({
      reason: "Biometrisches Entsperren aktivieren",
      allowDeviceCredential: true,
    });
  }
  await Preferences.set({ key: biometricPreferenceKey, value: String(enabled) });
}

export async function getBiometricUnlockEnabled(): Promise<boolean> {
  if (!isNativeApp()) return false;
  const stored = await Preferences.get({ key: biometricPreferenceKey });
  return stored.value === "true";
}
